import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import { EnvService } from 'src/app/env.service';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { StorageService } from 'src/app/storage.service';
import { environment } from 'src/environments/environment';

export type ConversationMessage =
  | { role: 'user'; type: 'text'; content: string; files?: string[] }
  | { role: 'assistant'; type: 'text'; content: string; files?: string[] }
  | { role: 'system'; type: 'text'; content: string; files?: string[] }
  | { role: 'user'; type: 'image_request'; content: string; files?: string[] }
  | { role: 'assistant'; type: 'image'; content: { prompt: string; imageUrl: string }; files?: string[] }
  | { role: 'user'; type: 'quiz_request'; content: string; context: string; files?: string[] }
  | { role: 'assistant'; type: 'quiz'; content: QuizContent; files?: string[] }
  | { role: 'user'; type: 'content_request'; content: string; context?: string; files?: string[] }
  | { role: 'assistant'; type: 'content_response'; _id?: string; content: { title: string; html: string }; files?: string[] }
  | { role: 'user'; type: 'mermaid_request'; content: string; context: string; files?: string[] }
  | { role: 'assistant'; type: 'mermaid'; _id?: string; content: { title: string; mermaid: string }; files?: string[] };

export interface QuizData {
  title: string;
  questions: {
    text: string;
    type?: string; // 'multiple-choice' | 'free-text'
    options?: string[];
    correctAnswerIndex?: number;
    correctAnswerIndices?: number[];
    explanation?: string;
  }[];
}

export interface QuizContent {
  _id?: string;
  en: QuizData;
  fr: QuizData;
  status?: 'setup' | 'draft' | 'ready';
  subject?: string;
  count?: number;
  difficulty?: string;
  context?: string;
  mediaContent?: { url: string; type: string; filename?: string }[];
  attachedContentIds?: string[];
}

export interface AttachedFile {
  name: string;
  url: string;
  type: string;
  size: number;
}

export interface AiGenerationSettings {
  allowAiSearch?: boolean;
  aiSkillztankBoundary?: boolean;
  searchAllContent?: boolean;
  allowedSearchContentIds?: string[];
  courseId?: string;
  aspectRatio?: string;
  model?: string;
}

export interface Conversation {
  _id: string;
  contentId: string;
  userId: string;
  messages: ConversationMessage[];
  __v: number;
  createdAt: Date;
  updatedAt: Date;
}

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
  }),
};

@Injectable({
  providedIn: 'root',
})
export class ConversationService {
  private env = inject(EnvService);
  private http = inject(HttpClient);
  private storageService = inject(StorageService);
  private toast = inject(ToastService);
  private translate = inject(TranslateService);

  private readonly url = this.env.apiUrl;
  private readonly userId: string;

  constructor() {
    const userData = JSON.parse(localStorage.getItem('userData'));
    this.userId = userData?.id;
  }

  private fetchAndStoreConversation(contentId: string): Observable<Conversation | null> {
    return this.http.get<Conversation>(`${this.url}conversations/${contentId}`, httpOptions).pipe(
      tap((conversation) => {
        if (conversation) {
          this.storageService.store('conversations', conversation);
        }
      }),
      catchError(() => of(null))
    );
  }

  syncConversationIfNeeded(contentId: string): void {
    if (!this.userId) return;
    const conversationId = `${contentId}_${this.userId}`;

    this.getConversationFromCache(conversationId)
      .pipe(
        take(1),
        switchMap((localConversation) => {
          const localVersion = localConversation ? localConversation.__v : 0;
          return this.http
            .get<{ __v: number }>(`${this.url}conversations/${contentId}/version`, httpOptions)
            .pipe(map((serverVersionInfo) => ({ serverVersion: serverVersionInfo.__v, localVersion })));
        }),
        switchMap(({ serverVersion, localVersion }) => {
          if (serverVersion > localVersion) {
            return this.fetchAndStoreConversation(contentId);
          }
          return of(null);
        })
      )
      .subscribe();
  }

  addMessageAndGenerateResponse(
    contentId: string,
    message: ConversationMessage,
    context: string,
    attachedFiles = [],
    attachedContentIds: string[] = [],
    settings: AiGenerationSettings = {}
  ): Observable<Conversation> {
    if (!this.userId) return of(null);
    this.updateLocalConversation(contentId, message);

    const payload = { contentId, message, context, linkPrefix: environment.gsLinkPrefix, appName: this.env.appName, files: attachedFiles, attachedContentIds, settings };

    return this.http.post<Conversation>(`${this.url}conversations`, payload, httpOptions).pipe(
      tap((updatedConversation) => {
        this.storageService.store('conversations', updatedConversation);
      }),
      catchError((err) => {
        this.toast.snackbar.open(this.translate.instant('Snackbar.Error'), 'Ok', { duration: 2000 });
        throw err;
      })
    );
  }

  addMessage(contentId: string, message: ConversationMessage, templateID: string): Observable<Conversation> {
    if (!this.userId) return of(null);
    this.updateLocalConversation(contentId, message);

    console.log('Adding message to conversation:', { contentId, message, templateID });
    const payload = { contentId, message, templateID, linkPrefix: environment.gsLinkPrefix, appName: this.env.appName };

    return this.http.post<Conversation>(`${this.url}conversations/message`, payload, httpOptions).pipe(
      tap((updatedConversation) => {
        console.log('Received updated conversation from server:', updatedConversation);
        this.storageService.store('conversations', updatedConversation);
      }),
      catchError((err) => {
        this.toast.snackbar.open(this.translate.instant('Snackbar.Error'), 'Ok', { duration: 2000 });
        throw err;
      })
    );
  }

  generateQuiz(
    contentId: string,
    subject: string,
    count: number,
    difficulty: string,
    context: string,
    fullContentText: string,
    attachedContentIds?: string[],
    mediaContexts?: { url: string; type: string; filename: string }[],
    draftId?: string,
    quizType?: string
  ): Observable<QuizContent> {
    if (!this.userId) return of(null);

    // Construct the prompt message
    const message: ConversationMessage = {
      role: 'user',
      type: 'quiz_request',
      content: `Generate a quiz about ${subject}`,
      context: JSON.stringify({ subject, count, difficulty, context, fullContentText, quizType }),
    };

    const payload: Record<string, unknown> = { contentId, message, linkPrefix: environment.gsLinkPrefix, appName: this.env.appName, attachedContentIds };
    if (draftId) {
      payload['draftId'] = draftId;
    }
    if (mediaContexts && mediaContexts.length > 0) {
      payload['mediaContexts'] = mediaContexts;
    }

    return this.http.post<QuizContent>(`${this.url}conversations/quiz`, payload, httpOptions).pipe(
      catchError((err) => {
        this.toast.snackbar.open(this.translate.instant('Snackbar.Error'), 'Ok', { duration: 2000 });
        throw err;
      })
    );
  }

  getQuizDraft(contentId: string): Observable<any> {
    return this.http.get(`${this.url}conversations/quiz/draft/${contentId}`, httpOptions);
  }

  getQuizDrafts(contentId: string): Observable<any[]> {
    return this.http.get<any[]>(`${this.url}conversations/quiz/drafts/${contentId}`, httpOptions);
  }

  getQuizDraftById(draftId: string): Observable<any> {
    return this.http.get(`${this.url}conversations/quiz/${draftId}`, httpOptions);
  }

  createQuizDraft(contentId: string, quizContent?: QuizContent | any, setupParams?: any): Observable<any> {
    return this.http.post(`${this.url}conversations/quiz/draft`, { contentId, quizContent, ...setupParams }, httpOptions);
  }

  updateQuizDraft(draftId: string, payload: { quizContent?: QuizContent; modifiedLang?: string } & any): Observable<any> {
    return this.http.put(`${this.url}conversations/quiz/${draftId}`, payload, httpOptions);
  }

  translateQuizDraft(draftId: string, sourceLang: string): Observable<QuizContent> {
    return this.http.post<QuizContent>(`${this.url}conversations/quiz/${draftId}/translate`, { sourceLang }, httpOptions);
  }

  deleteQuizDraft(draftId: string): Observable<any> {
    return this.http.delete(`${this.url}conversations/quiz/${draftId}`, httpOptions);
  }

  evaluateQuiz(questions: any[], answers: any[]): Observable<any> {
    return this.http.post<any>(`${this.url}conversations/quiz/evaluate`, { questions, answers }, httpOptions);
  }

  getConversationFromCache(contentId: string): Observable<Conversation | undefined> {
    if (!this.userId) return of(undefined);
    const conversationId = `${contentId}_${this.userId}`;
    return this.storageService.find$('conversations', [conversationId]).pipe(map((conversations) => conversations[0]));
  }

  private async updateLocalConversation(contentId: string, message: ConversationMessage): Promise<void> {
    if (!this.userId) return;
    const conversationId = `${contentId}_${this.userId}`;
    const conversation = (await this.storageService.find('conversations', [conversationId]))[0];

    if (conversation) {
      conversation.messages.push(message);
      this.storageService.store('conversations', conversation);
    } else {
      const newConversation: Conversation = {
        _id: conversationId,
        contentId: contentId,
        userId: this.userId,
        messages: [message],
        __v: 0,
        createdAt: new Date(),
        updatedAt: new Date(),
      };
      this.storageService.store('conversations', newConversation);
    }
  }

  clearConversation(contentId: string): Observable<any> {
    if (!this.userId) return of(null);
    const conversationId = `${contentId}_${this.userId}`;

    return this.http.delete(`${this.url}conversations/${contentId}`, httpOptions).pipe(
      tap(() => {
        this.storageService.remove('conversations', [conversationId]);
      })
    );
  }

  updateMessage(contentId: string, messageId: string, content: any): Observable<any> {
    if (!this.userId) return of(null);
    const conversationId = `${contentId}_${this.userId}`;

    this.updateLocalMessage(contentId, messageId, content);

    return this.http.put(`${this.url}conversations/${conversationId}/messages/${messageId}`, { content }, httpOptions).pipe(
      catchError((err) => {
        this.toast.snackbar.open(this.translate.instant('Snackbar.Error'), 'Ok', { duration: 2000 });
        throw err;
      })
    );
  }

  private async updateLocalMessage(contentId: string, messageId: string, content: any): Promise<void> {
    if (!this.userId) return;
    const conversationId = `${contentId}_${this.userId}`;
    const conversation = (await this.storageService.find('conversations', [conversationId]))[0];

    if (conversation) {
      const message = conversation.messages.find((m: any) => m._id === messageId);
      if (message) {
        message.content = content;
        this.storageService.store('conversations', conversation);
      }
    }
  }
}
